import React from "react";
import { advanceDemand, getDemandStatus } from "../api/demandsClient";
import { stageLabel } from "../constants/demandStages";

/**
 * "This stage is done — move on" control.
 *
 * Every stage screen needs one, and until now only two had anything equivalent:
 * Discovery's Complete modal and Security's "Pass → TAB" button. POC and
 * Requirements had none at all, so a demand that reached either stopped there —
 * the backend saved its work but nothing ever changed current_stage, and it never
 * appeared on the following screen. That was the phase-2 dead end.
 *
 * The target comes from the server (GET /{id}/status -> nextStage) rather than
 * being hardcoded per screen, so POC-skipping stays a server decision.
 */
export default function AdvanceStageBar({ demandId, requirement, onAdvanced }) {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [done, setDone] = React.useState("");

  const load = React.useCallback(async () => {
    if (!demandId) return;
    try {
      setError("");
      setStatus(await getDemandStatus(demandId));
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [demandId]);

  React.useEffect(() => { load(); }, [load]);

  const onAdvance = async () => {
    setBusy(true);
    setError("");
    setDone("");
    try {
      const res = await advanceDemand(demandId);
      setDone(res.message || `Moved to ${res.to}.`);
      await load();
      onAdvanced?.(res);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!demandId) return null;

  const next = status?.nextStage;
  // `requirement` lets a screen say why it isn't ready yet (e.g. no spec saved),
  // rather than letting someone advance a stage that produced nothing.
  const blocked = requirement && !requirement.met;

  return (
    <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm">
          <div className="font-medium text-gray-900">
            Current stage: {stageLabel(status?.current_stage)}
            {status?.stage_status ? (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({status.stage_status})
              </span>
            ) : null}
          </div>
          {next ? (
            <div className="text-xs text-gray-500">Next: {stageLabel(next)}</div>
          ) : (
            <div className="text-xs text-gray-500">This is the final stage.</div>
          )}
          {blocked && (
            <div className="mt-1 text-xs text-amber-700">{requirement.message}</div>
          )}
        </div>

        <button
          onClick={onAdvance}
          disabled={busy || !next || blocked}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy ? "Moving…" : next ? `Complete & move to ${stageLabel(next)}` : "No next stage"}
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </div>
      )}
      {done && (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          {done}
        </div>
      )}
    </div>
  );
}
